import { useCallback, useEffect, useState } from 'react';
import { IconButton, Stack, Button, useTheme, Tooltip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { fetchAPI } from '../../../api/request';
import Loading from '../../common/Loading.component';
import Alert from '../../common/Alert.component';
import AddView from '../../../views/Add.view';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import DrawerComponent from '../../common/Drawer.component';

const ItemAdd = ({
  setSuccessAdd = () => {},
  module: { create, code },
  idLogbook,
  productsList,
  disabled = false,
  viewIconAdd = false,
}) => {
  const theme = useTheme();
  const { id } = useParams();
  const { jwt } = useSelector((state) => state.login);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [conf, setConf] = useState(create?.conf);
  const [openAdd, setOpenAdd] = useState(false);

  useEffect(() => {
    const idRandom = uuidv4();
    let fields = create.conf.fields.map((item) => {
      let newItem = { ...item, id: `${item.id}_${idRandom}` };
      return newItem;
    });
    setConf({ ...create.conf, fields: fields });
  }, [create]);

  const onError = useCallback(() => {
    setSuccess(false);
    setLoading(false);
    setError(true);
    // eslint-disable-next-line
  }, []);

  const onSuccess = useCallback(() => {
    setSuccess(true);
    setError(false);
    setLoading(false);
    setSuccessAdd((prev) => !prev);
    localStorage.setItem(`isAddItemOpen${code}`, true);
    // eslint-disable-next-line
  }, [setLoading, setSuccess, setOpenAdd, code]);

  const onAddToLogbook = useCallback(
    (resp) => {
      if (idLogbook && !_.isEmpty(productsList)) {
        const id_list_items = _.concat(productsList, resp?.id);

        const data = {
          id_list_items: id_list_items,
        };
        fetchAPI(
          { path: `/logbooks/${idLogbook}`, method: 'PUT', auth: true },
          {},
          '',
          { data: data },
          jwt
        )
          .then(onSuccess)
          .catch(onError);
      } else {
        onSuccess();
      }
    },
    [idLogbook, jwt, onSuccess, onError, productsList]
  );

  const onSubmit = useCallback(
    async (values) => {
      setSuccess(false);
      setError(false);
      setLoading(true);

      const data = {
        id_list: id,
        available: values.available?.id,
        quantity: !_.isEmpty(values.quantity) ? values.quantity : null,
        quantity_type: values.quantity_type?.id,
        category: values.category?.id,
        note: values?.note,
        internal_note: values?.internal_note,
        id_item: values?.id_item?.id,
      };
      const formData = new FormData();

      if (values?.attachment) {
        Array.from(values?.attachment)?.map((file) =>
          formData.append('files.attachment', file, file?.name)
        );
      }

      formData.append('data', JSON.stringify(data));

      fetchAPI({ path: '/list-items', method: 'POST' }, {}, '', formData, jwt)
        .then((resp) => onAddToLogbook(resp?.data))
        .catch(onError);
    },
    [setLoading, jwt, onError, onAddToLogbook, id]
  );

  const onClose = useCallback(() => {
    setOpenAdd(false);
    localStorage.setItem(`isAddItemOpen${code}`, false);
  }, [setOpenAdd, code]);

  useEffect(() => {
    if (localStorage.getItem(`isAddItemOpen${code}`) === 'true')
      setOpenAdd(true);
  }, [code]);

  return (
    <Stack>
      {loading && <Loading />}
      {success && <Alert severity='success'>{'Success: Add'}</Alert>}
      {error && <Alert severity='error'>{'An error has occurred'}</Alert>}
      <DrawerComponent
        defaultOpen={openAdd}
        onClose={onClose}
        autoFocus={true}
        button={
          viewIconAdd ? (
            <Tooltip title={'Add Item'}>
              <IconButton
                sx={{
                  backgroundColor: theme.palette.primary.dark,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'white',
                    color: theme.palette.primary.dark,
                  },
                }}
                autoFocus
              >
                <Add />
              </IconButton>
            </Tooltip>
          ) : (
            <Button variant='contained' sx={{ margin: 1 }} disabled={disabled}>
              Add Item
            </Button>
          )
        }
      >
        {conf && (
          <AddView
            conf={conf}
            onAddSubmit={onSubmit}
            onAddError={onError}
            onAddSuccess={onSuccess}
            disabled={loading}
          />
        )}
      </DrawerComponent>
    </Stack>
  );
};

export default ItemAdd;
